import React, { useState } from 'react';
import { FiPhone, FiMail, FiMapPin, FiSend, FiClock, FiCheck } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { contactFAQs } from '../data/faqs';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nMessage: ${formData.message}`);
    window.open(`https://wa.me/919820088292?text=${msg}`, '_blank');
  };

  const handleShareLocation = () => {
    const mapsUrl = 'https://maps.google.com/?q=18.764728,73.435652';
    const message =
      `📍 *Royal Garden Villas* — Lonavala\n\n` +
      `Here's the exact location on Google Maps:\n${mapsUrl}\n\n` +
      `Near Pinewood Park, Tata Dam Road, Lonavala 410401\n\n` +
      `📞 Need help finding us? Call +91 91378 93277`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="contact-page" className="pt-28">
      <SEO
        title="Contact Royal Garden Villas, Lonavala | +91 98200 88292"
        description="Get in touch with Royal Garden Villas for villa bookings, group enquiries and corporate retreats in Lonavala. Call +91 98200 88292 or WhatsApp directly."
        path="/contact"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' },
        ]}
      />
      <section className="py-16 md:py-20 bg-[#1B2432]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Contact <span className="text-[#F57C20] italic">Us</span>
          </h1>
          <p className="text-white/60 mt-4 max-w-xl text-sm">
            Planning a celebration, corporate event, or getaway? We&apos;re here to help you find the perfect villa.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send Us a <span className="text-[#F57C20] italic">Message</span>
              </h2>
              <p className="text-[#6B7280] text-sm mb-8">
                Fill out the form below and we&apos;ll get back to you within 1 hour during business hours.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <input type="text" placeholder="Your Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                <input type="email" placeholder="Email Address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                <input type="tel" placeholder="Phone Number" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]" />
                <textarea placeholder="Your Message (villa type, dates, guests, occasion)" value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} rows={4} required className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm placeholder-[#9CA3AF] focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20] resize-none" />
                <button type="submit" data-testid="contact-submit-btn" className="bg-[#F57C20] text-white px-8 py-4 text-sm font-semibold rounded-lg hover:bg-[#E06A10] transition-all flex items-center gap-2 hover:shadow-lg hover:shadow-orange-500/20">
                  Send via WhatsApp <FiSend className="w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Contact Details</h3>
                <div className="space-y-5">
                  {[
                    { icon: FiPhone, title: "Phone / WhatsApp", value: "+91 9820088292", href: "tel:+919820088292", sub: "For bookings, enquiries & support" },
                    { icon: FiMail, title: "Email", value: "royalgardenlonavala@gmail.com", href: "mailto:royalgardenlonavala@gmail.com" },
                    { icon: FiMapPin, title: "Address", value: "Royal Garden Villas, near Pinewood Park, Inside lane of Lucky Wax Museum, Tata Dam Road, Lonavala 410401", sub: "Approx. 5 min from Mapro Gardens" },
                    { icon: FiClock, title: "Availability", value: "7 days a week, 9 AM - 9 PM", sub: "Check-in: 1 PM | Check-out: 10 AM" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#1B2432] flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-[#F57C20]" />
                      </div>
                      <div>
                        <p className="text-[#1A1A2E] text-sm font-semibold mb-0.5">{item.title}</p>
                        {item.href ? (
                          <a href={item.href} className="text-[#6B7280] text-sm hover:text-[#F57C20] transition-colors">{item.value}</a>
                        ) : (
                          <p className="text-[#6B7280] text-sm">{item.value}</p>
                        )}
                        {item.sub && <p className="text-[#9CA3AF] text-xs mt-0.5">{item.sub}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Guest ID Submission</h3>
                <p className="text-[#6B7280] text-sm mb-3">Government-issued photo ID required for all guests.</p>
                <div className="space-y-2">
                  {["Submit via WhatsApp: +91 9137893277", "Must be submitted 1 day before check-in", "Accepted: Aadhar, PAN, Passport, Driving License"].map((rule, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <FiCheck className="w-3 h-3 text-[#F57C20] mt-1 flex-shrink-0" />
                      <span className="text-[#6B7280] text-xs">{rule}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 border border-gray-100">
                <h3 className="text-lg font-bold text-[#1A1A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>How to Reach</h3>
                <div className="text-[#6B7280] text-xs space-y-2">
                  <p><strong className="text-[#1A1A2E]">From Mumbai:</strong> Via Mumbai-Pune Expressway. ~83 km, 2 hours.</p>
                  <p><strong className="text-[#1A1A2E]">From Pune:</strong> Via Old Highway or Expressway. ~64 km, 1.5 hours.</p>
                  <p><strong className="text-[#1A1A2E]">By Train:</strong> Lonavala Railway Station (5 km).</p>
                  <p><strong className="text-[#1A1A2E]">Nearest Airport:</strong> Pune Airport (70 km) or Mumbai Airport (95 km).</p>
                </div>
              </div>

              {/* Google Map */}
              <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1500!2d73.435652!3d18.764728!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDQ1JzUzLjAiTiA3M8KwMjYnMDguMyJF!5e0!3m2!1sen!2sin!4v1"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Royal Garden Villas Location"
                ></iframe>
                <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <a
                    href="https://maps.google.com/?q=18.764728,73.435652"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-open-maps-link"
                    className="text-[#F57C20] text-sm font-semibold hover:underline flex items-center gap-2"
                  >
                    <FiMapPin className="w-4 h-4" /> Open in Google Maps
                  </a>
                  <button
                    type="button"
                    onClick={handleShareLocation}
                    data-testid="contact-share-location-whatsapp-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] active:bg-[#179a4b] transition-colors text-white text-xs font-semibold px-4 py-2 rounded-full shadow-sm"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    Share location on WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CONTACT FAQ (with JSON-LD schema for AI-search) ===================== */}
      <FAQSection
        title="Contact &amp; Location FAQ"
        subtitle="How to reach us, where we are, and what to expect."
        testid="contact-faq"
        items={contactFAQs}
      />
    </div>
  );
};

export default Contact;
